import React, { useMemo } from 'react';
import './ProgressCharts.css';

export const ProgressCharts = ({ reviewData, allDecks }) => {
  // Calculate data for charts
  const chartData = useMemo(() => {
    const last30Days = [];
    const today = new Date();

    // Generate last 30 days
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      last30Days.push({
        date: dateStr,
        dateObj: date,
        reviews: 0,
        correct: 0,
        incorrect: 0
      });
    }

    // Count reviews per day
    Object.values(reviewData).forEach(review => {
      if (review && review.lastReviewed) {
        const reviewDate = new Date(review.lastReviewed);
        const reviewDateStr = reviewDate.toISOString().split('T')[0];

        const dayData = last30Days.find(d => d.date === reviewDateStr);
        if (dayData) {
          dayData.reviews++;
          if (review.rating === 'easy' || review.rating === 'medium') {
            dayData.correct++;
          } else {
            dayData.incorrect++;
          }
        }
      }
    });

    return last30Days;
  }, [reviewData]);

  const maxReviews = Math.max(...chartData.map(d => d.reviews), 10);
  const chartHeight = 200;
  const chartWidth = 600;
  const barWidth = chartWidth / chartData.length;

  const getWeekLabel = (dateObj) => {
    const day = dateObj.getDate();
    const month = dateObj.getMonth() + 1;
    if (day === 1 || dateObj.getDay() === 1) {
      return `${month}/${day}`;
    }
    return '';
  };

  // Accuracy over time
  const accuracyData = chartData.map(d => ({
    date: d.date,
    dateObj: d.dateObj,
    accuracy: d.reviews > 0 ? (d.correct / d.reviews) * 100 : 0
  }));

  // Generate SVG path for line chart
  const generateLinePath = (data, key, max = 100) => {
    if (data.length === 0) return '';

    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * chartWidth;
      const y = chartHeight - (d[key] / max) * chartHeight;
      return `${x},${y}`;
    });

    return `M ${points.join(' L ')}`;
  };

  const accuracyPath = generateLinePath(accuracyData, 'accuracy', 100);

  // Calculate heatmap data (last 12 weeks)
  const heatmapData = useMemo(() => {
    const weeks = [];
    const today = new Date();

    // Generate 12 weeks x 7 days
    for (let week = 11; week >= 0; week--) {
      const weekDays = [];
      for (let day = 0; day < 7; day++) {
        const date = new Date(today);
        date.setDate(date.getDate() - (week * 7 + (6 - day)));
        const dateStr = date.toISOString().split('T')[0];

        const reviews = Object.values(reviewData).filter(review => {
          if (!review || !review.lastReviewed) return false;
          const reviewDate = new Date(review.lastReviewed).toISOString().split('T')[0];
          return reviewDate === dateStr;
        }).length;

        weekDays.push({
          date: dateStr,
          dateObj: date,
          reviews,
          intensity: reviews === 0 ? 0 : Math.min(Math.ceil(reviews / 5), 4)
        });
      }
      weeks.push(weekDays);
    }

    return weeks;
  }, [reviewData]);

  const getHeatmapColor = (intensity) => {
    const colors = [
      '#f0f0f0', // 0 - no activity
      '#c6e48b', // 1 - low
      '#7bc96f', // 2 - medium
      '#239a3b', // 3 - high
      '#196127'  // 4 - very high
    ];
    return colors[intensity] || colors[0];
  };

  return (
    <div className="progress-charts">
      {/* Daily Reviews Bar Chart */}
      <div className="chart-section">
        <h3>Daily Reviews (Last 30 Days)</h3>
        <svg className="bar-chart" viewBox={`0 0 ${chartWidth} ${chartHeight + 40}`}>
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(percent => {
            const y = chartHeight - (percent / 100) * chartHeight;
            return (
              <g key={percent}>
                <line
                  x1="0"
                  y1={y}
                  x2={chartWidth}
                  y2={y}
                  stroke="#e5e5e5"
                  strokeWidth="1"
                />
                <text
                  x="-5"
                  y={y + 4}
                  fontSize="10"
                  fill="#888"
                  textAnchor="end"
                >
                  {Math.round((percent / 100) * maxReviews)}
                </text>
              </g>
            );
          })}

          {/* Bars */}
          {chartData.map((d, i) => {
            const barHeight = (d.reviews / maxReviews) * chartHeight;
            const x = i * barWidth;
            const y = chartHeight - barHeight;

            return (
              <g key={d.date}>
                <rect
                  x={x + 2}
                  y={y}
                  width={barWidth - 4}
                  height={barHeight}
                  fill="#3b82f6"
                  opacity="0.8"
                  rx="2"
                />
                {getWeekLabel(d.dateObj) && (
                  <text
                    x={x + barWidth / 2}
                    y={chartHeight + 20}
                    fontSize="10"
                    fill="#666"
                    textAnchor="middle"
                  >
                    {getWeekLabel(d.dateObj)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Accuracy Line Chart */}
      <div className="chart-section">
        <h3>Accuracy Trend (Last 30 Days)</h3>
        <svg className="line-chart" viewBox={`0 0 ${chartWidth} ${chartHeight + 40}`}>
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(percent => {
            const y = chartHeight - (percent / 100) * chartHeight;
            return (
              <g key={percent}>
                <line
                  x1="0"
                  y1={y}
                  x2={chartWidth}
                  y2={y}
                  stroke="#e5e5e5"
                  strokeWidth="1"
                />
                <text
                  x="-5"
                  y={y + 4}
                  fontSize="10"
                  fill="#888"
                  textAnchor="end"
                >
                  {percent}%
                </text>
              </g>
            );
          })}

          {/* Line path */}
          <path
            d={accuracyPath}
            fill="none"
            stroke="#22c55e"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points */}
          {accuracyData.map((d, i) => {
            if (d.accuracy === 0) return null;
            const x = (i / (accuracyData.length - 1)) * chartWidth;
            const y = chartHeight - (d.accuracy / 100) * chartHeight;

            return (
              <circle
                key={d.date}
                cx={x}
                cy={y}
                r="4"
                fill="#22c55e"
              />
            );
          })}
        </svg>
      </div>

      {/* Activity Heatmap */}
      <div className="chart-section">
        <h3>Study Activity Heatmap (Last 12 Weeks)</h3>
        <div className="heatmap">
          <div className="heatmap-labels">
            <div className="heatmap-label">Sun</div>
            <div className="heatmap-label">Mon</div>
            <div className="heatmap-label">Tue</div>
            <div className="heatmap-label">Wed</div>
            <div className="heatmap-label">Thu</div>
            <div className="heatmap-label">Fri</div>
            <div className="heatmap-label">Sat</div>
          </div>
          <div className="heatmap-grid">
            {heatmapData.map((week, weekIdx) => (
              <div key={weekIdx} className="heatmap-week">
                {week.map((day, dayIdx) => (
                  <div
                    key={day.date}
                    className="heatmap-cell"
                    style={{ backgroundColor: getHeatmapColor(day.intensity) }}
                    title={`${day.date}: ${day.reviews} reviews`}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className="heatmap-legend">
            <span className="heatmap-legend-label">Less</span>
            {[0, 1, 2, 3, 4].map(i => (
              <div
                key={i}
                className="heatmap-legend-cell"
                style={{ backgroundColor: getHeatmapColor(i) }}
              />
            ))}
            <span className="heatmap-legend-label">More</span>
          </div>
        </div>
      </div>
    </div>
  );
};
